import moment from 'moment';
import { useEffect, useRef } from 'react';
import { pingAiApi } from '@/shared/api';
import { TAB_ENUM } from './pingAiApiSection.c';

export type PingAiApiSectionProps = {
  profileId: string;
  activeTab: string;
  isMigrating?: boolean;
};

const PingAiApiSection = ({
  profileId,
  activeTab,
  isMigrating = false,
}: PingAiApiSectionProps): null => {
  const pingApiTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const startTime = useRef<moment.Moment | null>(null);
  const currentActiveTab = useRef(activeTab);

  /**
   * Pings the AI backend for the profile currently open. Translates the active tab ref through the tab enum and omits the tab entirely while a migration is running.
   *
   * @returns Nothing.
   */
  const handleCallPingAiApi = () => {
    const getTab = TAB_ENUM[currentActiveTab.current as unknown as keyof typeof TAB_ENUM];
    pingAiApi(profileId, isMigrating ? undefined : getTab);
  };

  /**
   * Debounces user activity down to at most one AI ping per minute. Records the first activity moment and schedules the ping a minute later, then clears the recorded moment so the next activity can open a new window.
   *
   * @returns Nothing.
   */
  const updateIsActivity = () => {
    if (!startTime.current) {
      startTime.current = moment();
      pingApiTimerRef.current = setTimeout(() => {
        handleCallPingAiApi();
        startTime.current = null;
      }, 60 * 1000); // *: 60 seconds
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', updateIsActivity);
    document.addEventListener('keyup', updateIsActivity);
    document.addEventListener('scroll', updateIsActivity);

    if (activeTab !== currentActiveTab.current) {
      currentActiveTab.current = activeTab;
    }

    return () => {
      document.removeEventListener('mousemove', updateIsActivity);
      document.removeEventListener('keyup', updateIsActivity);
      document.removeEventListener('scroll', updateIsActivity);
    };
  }, [profileId, isMigrating, activeTab]);

  useEffect(() => {
    handleCallPingAiApi();
  }, []);

  return null;
};

export default PingAiApiSection;
